import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useInstantLayoutTransition, useMotionValue, type MotionValue } from 'framer-motion'
import { GlobalEvents } from '@highlight/ipc'
import type { SidepanelSide } from '@shared/sidepanel/side'
import { useSidepanelSide, useSetSidepanelSide, useSetSidepanelGeometry, useSetSidepanelRepositioning } from '../atoms'
import { clampSidepanelVerticalOffset, type SidepanelVerticalLayout } from '../sidepanel-vertical-layout'
import { useDragCursor } from './use-drag-cursor'
import { sendOverlayDragPing } from '@renderer/overlay/utils/drag-ping'
import { useHoldOverlayDisplayKey } from '@renderer/overlay/hooks/use-overlay-display-key'

/** Pointer must travel this far (px) before a pointerdown becomes a drag. */
const DRAG_ACTIVATION_DISTANCE = 6

// Hysteresis around the screen midline so the highlighted drop zone doesn't
// flicker while the cursor hovers near center.
const FLIP_TO_RIGHT_FRACTION = 0.53
const FLIP_TO_LEFT_FRACTION = 0.47

export interface SidepanelDragStartOptions {
  /** Fires when the gesture ends — whether or not it crossed the drag threshold. */
  onSettle?: (result: { activated: boolean }) => void
}

export interface SidepanelDragControls {
  start(event: ReactPointerEvent<Element> | PointerEvent, options?: SidepanelDragStartOptions): void
}

/**
 * Module-level ref so the toolbar drag surface (rendered deep inside the panel
 * header) can start a drag owned by the root `Sidepanel` component — same
 * singleton pattern as the Mini's `miniDragControlsRef`.
 */
export const sidepanelDragControlsRef: { current: SidepanelDragControls | null } = {
  current: null,
}

interface SidepanelDragState {
  /** True from the activation threshold until release. The drop zones show for the whole gesture. */
  isDragging: boolean
  /** True while the drag reads as horizontal, so a drop re-docks the panel. */
  isDocking: boolean
  /** True while the panel is following the pointer vertically. */
  isRepositioning: boolean
  /** Live vertical offset from the panel's resolved top, in px. */
  dragY: MotionValue<number>
  /** Which drop zone the cursor is over. Only meaningful while dragging. */
  hoveredSide: SidepanelSide
  /** Live cursor position driving the drag preview without re-rendering. */
  pointerX: MotionValue<number>
  pointerY: MotionValue<number>
  startDrag: (event: ReactPointerEvent<Element> | PointerEvent, options?: SidepanelDragStartOptions) => void
}

/**
 * Drag-to-reposition for the sidepanel: dragging the panel header shows drop
 * zones on the left/right screen edges as soon as the drag activates and, if
 * the gesture reads as horizontal on drop, re-anchors the panel to the chosen
 * side. Throttled drag pings let the main process move the whole
 * overlay window to the display under the cursor (the same cross-display
 * mechanism the Mini uses), so the drop zones follow the cursor across
 * monitors.
 *
 * The panel follows the pointer vertically while it is shorter than the
 * available band and the gesture is still a vertical reposition. Once the
 * drag enters the docking preview (clearly horizontal), Y locks so the live
 * panel does not keep sliding while the cursor aims at a drop zone;
 * horizontally the panel stays put and only the drop zones react.
 *
 * Perf: still no per-frame renders — pointermove writes the vertical offset to
 * a motion value (which bypasses React) and only flips `hoveredSide` state when
 * the cursor crosses the (hysteresis-guarded) midline.
 */
export function useSidepanelDrag(layout: SidepanelVerticalLayout): SidepanelDragState {
  const side = useSidepanelSide()
  const setSide = useSetSidepanelSide()
  const setGeometry = useSetSidepanelGeometry()
  const holdDisplayKey = useHoldOverlayDisplayKey()
  const startInstantLayout = useInstantLayoutTransition()

  const [isDragging, setIsDragging] = useState(false)
  const [isDocking, setIsDocking] = useState(false)
  const [isRepositioning, setIsRepositioning] = useState(false)
  const [hoveredSide, setHoveredSide] = useState<SidepanelSide>(side)

  const dragY = useMotionValue(0)
  const pointerX = useMotionValue(0)
  const pointerY = useMotionValue(0)

  // `dragY` bypasses React, so this flag is all a surface outside this subtree
  // (the quick tour card) gets to tell that the panel is moving.
  const setRepositioningAtom = useSetSidepanelRepositioning()
  useEffect(() => {
    setRepositioningAtom(isRepositioning)
  }, [isRepositioning, setRepositioningAtom])

  const hoveredSideRef = useRef<SidepanelSide>(side)
  const isDockingRef = useRef(false)
  const cleanupRef = useRef<(() => void) | null>(null)
  // Read at pointerdown, not closed over: `startDrag` must not be re-created
  // (and re-registered on the header) every time the layout re-resolves.
  const layoutRef = useRef(layout)
  layoutRef.current = layout

  useEffect(() => {
    return () => {
      cleanupRef.current?.()
    }
  }, [])

  useDragCursor(isDragging, 'grabbing')

  const startDrag = useCallback(
    (e: ReactPointerEvent<Element> | PointerEvent, options?: SidepanelDragStartOptions) => {
      if (cleanupRef.current) {
        return
      }

      const startX = e.clientX
      const startY = e.clientY
      let activated = false
      const onSettle = options?.onSettle

      const { bandTop, bandHeight, height, top: startTop } = layoutRef.current
      // At full band height there is nothing to move.
      const canReposition = height < bandHeight
      let committedOffset = startTop - bandTop
      // Re-based when leaving the docking preview so unlocking Y does not jump
      // to wherever the cursor drifted while the panel was locked.
      let verticalAnchorY = startY
      let verticalAnchorOffset = committedOffset

      // Start from the panel's current side so a sub-threshold drag is a no-op.
      hoveredSideRef.current = side
      setHoveredSide(side)
      pointerX.jump(startX)
      pointerY.jump(startY)

      const onPointerMove = (ev: PointerEvent) => {
        pointerX.set(ev.clientX)
        pointerY.set(ev.clientY)

        const dx = ev.clientX - startX
        const dy = ev.clientY - startY
        if (!activated) {
          if (dx * dx + dy * dy < DRAG_ACTIVATION_DISTANCE * DRAG_ACTIVATION_DISTANCE) {
            return
          }
          activated = true
          setIsDragging(true)
          if (canReposition) {
            setIsRepositioning(true)
          }
          // Lets main capture the display the drag started on, so a drop on
          // a different display can be treated as an explicit re-pin.
          GlobalEvents.emit('sidepanel:dragStart')
        }

        const nextIsDocking = Math.abs(dx) > Math.abs(dy)

        // Once the docking preview is showing, freeze Y so the live panel does
        // not keep tracking the cursor while the user aims at a drop zone.
        if (canReposition && !nextIsDocking) {
          if (isDockingRef.current) {
            verticalAnchorY = ev.clientY
            verticalAnchorOffset = committedOffset
          }
          committedOffset = clampSidepanelVerticalOffset(
            verticalAnchorOffset + (ev.clientY - verticalAnchorY),
            bandHeight,
            height,
          )
          // Motion value writes bypass React, so this stays a zero-re-render drag.
          dragY.set(bandTop + committedOffset - startTop)
        }

        if (nextIsDocking !== isDockingRef.current) {
          isDockingRef.current = nextIsDocking
          setIsDocking(nextIsDocking)
        }

        // Only a visible docking gesture may choose a new side. A vertical
        // reposition can drift across the screen's midpoint but must leave the
        // panel's current dock unchanged.
        if (nextIsDocking) {
          const fraction = ev.clientX / window.innerWidth
          if (hoveredSideRef.current === 'left' && fraction > FLIP_TO_RIGHT_FRACTION) {
            hoveredSideRef.current = 'right'
            setHoveredSide('right')
          } else if (hoveredSideRef.current === 'right' && fraction < FLIP_TO_LEFT_FRACTION) {
            hoveredSideRef.current = 'left'
            setHoveredSide('left')
          }
        }

        // Throttled ping so main can move the overlay to the cursor's display.
        void sendOverlayDragPing()
      }

      const endDrag = (commit: boolean) => {
        cleanup()
        // Settle before the early return so click-pairing (double-click center)
        // still learns that the gesture never became a drag.
        onSettle?.({ activated })
        if (!activated) {
          return
        }
        const dockedSide = isDockingRef.current ? hoveredSideRef.current : null
        setIsDragging(false)
        isDockingRef.current = false
        setIsDocking(false)

        // Held until the commit below: main's `overlay.displayId` broadcast
        // beats the ping reply, and an unheld key would resolve the
        // destination's previous height and offset for those frames.
        const releaseDisplayKey = holdDisplayKey()

        const settle = (dropDisplayId: number | null) => {
          if (commit) {
            // After the final drag ping has moved the window, main records the
            // drop display as the pinned-screen preference.
            GlobalEvents.emit('sidepanel:dragEnd')
            // Same trick as the width-resize commit: children using framer
            // `layout`/`layoutId` (reorderable sections, chat input) would
            // otherwise animate their position delta across the screen when the
            // anchor swaps. Instant layout applies the new positions in one
            // frame; the panel's own re-dock entrance handles the visible slide.
            //
            // The offset commits in the same batch: the re-dock's own x-entrance
            // covers the horizontal move, and a separately-committed y would read
            // as a second slide right after it.
            startInstantLayout(() => {
              if (dockedSide) {
                setSide(dockedSide)
              }
              if (canReposition) {
                setGeometry({ verticalOffset: committedOffset }, dropDisplayId)
              }
            })
          }
          // Releases in the same batch as the write above, so the key and the
          // geometry it resolves move to the drop display together.
          releaseDisplayKey()
          // Safe to zero unconditionally: the style drops `y` in the same commit
          // that `isRepositioning` goes false, so nothing renders the old offset.
          dragY.set(0)
          setIsRepositioning(false)
        }

        // Final ping catches a display change in the last <150ms of the drag,
        // and answers with the display the drop landed on. Settling in the
        // answer is what keeps the offset out of the wrong monitor's entry;
        // until it arrives the panel simply stays where the user let go.
        void sendOverlayDragPing({ force: true }).then(settle)
      }

      const onPointerUp = () => endDrag(true)
      // Pointer lost (system dialog, screen lock) — abandon without committing.
      const onPointerCancel = () => endDrag(false)

      const cleanup = () => {
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
        window.removeEventListener('pointercancel', onPointerCancel)
        cleanupRef.current = null
      }

      cleanupRef.current = cleanup
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
      window.addEventListener('pointercancel', onPointerCancel)
    },
    [side, setSide, setGeometry, holdDisplayKey, startInstantLayout, dragY, pointerX, pointerY],
  )

  return { isDragging, isDocking, isRepositioning, dragY, hoveredSide, pointerX, pointerY, startDrag }
}
